import {
  BuilderAdditionalProps,
  BuilderAttributes,
} from "@faire/builder-core/registerBuilderComponents/__internal__/BuilderTypes";
import {
  ProductTile,
  ProductTileProps,
} from "@faire/deprecated-retailer-visitor-shared/features/MarketplaceItems/ProductTile";
import {
  ILoggingContext,
  ProductTileType,
} from "@faire/deprecated-retailer-visitor-shared/features/MarketplaceItems/ProductTile/types";
import { generateProductLinkPropsForLoggedOutRetailer } from "@faire/deprecated-retailer-visitor-shared/lib/generateProductLinkPropsForLoggedOutRetailer";
import { useIsLoggedInRetailer } from "@faire/marketplace-lib/serialized-data/isLoggedInRetailer";
import { Surface } from "@faire/web-api--source/com/faire/logging/Surface";
import { useQuery_escape_hatch as useBulkFetchProductsQuery } from "@faire/web-api--source/endpoints/www/api/v3/layout/bulk-product-tiles/post-hooks";
import { ILeanProductTile } from "@faire/web-api--source/indigofair/data/ILeanProductTile";
import { ProductTileContainer } from "@faire/web-api--source/indigofair/data/ProductTileContainer";
import { ProductTileScreenContainer } from "@faire/web-api--source/indigofair/data/ProductTileScreenContainer";
import { Grid, IGridProps } from "@faire/web/slate/Layout";
import React from "react";
import { useLocation } from "react-router-dom";

import {
  deduplicateProductsOrBrands,
  MOCK_BRAND,
  MOCK_PRODUCT_TILE,
  shouldRenderProductionTile,
} from "./utils";

export interface IProductInput
  extends Pick<ProductTileProps, "type" | "loggingContext"> {
  tokenStaging: string;
  tokenProd: string;
}

interface IProps
  extends Omit<IGridProps, "children" | "columnTemplate">,
    BuilderAdditionalProps {
  products: IProductInput[];
  useCustomStagingToken: boolean;
  loggingContext: Pick<
    ILoggingContext["impressionProviderProps"],
    | "productTileContainer"
    | "screenContext"
    | "containerContext"
    | "contextToken"
    | "trackedLinkContainerName"
    | "isNewProductsCategory"
  >;
  attributes: BuilderAttributes;
}

export const BuilderRetailerProductTilesSection: React.FC<IProps> = ({
  products: productInputs,
  useCustomStagingToken,
  loggingContext,
  attributes,
  builderBlock,
  builderState,
  ...gridProps
}) => {
  const isLoggedIn = useIsLoggedInRetailer();
  const location = useLocation();
  const context =
    `${loggingContext.productTileContainer ?? ProductTileContainer.BUILDER_IO_GRID}|${loggingContext.contextToken}` as const;

  const renderMockTile =
    !useCustomStagingToken && !shouldRenderProductionTile();

  const visibleProductInputs = renderMockTile
    ? productInputs
    : deduplicateProductsOrBrands(productInputs, !shouldRenderProductionTile());

  const { data: liveProductTileData, isPending } = useBulkFetchProductsQuery(
    {
      product_tokens: visibleProductInputs.map((product) =>
        !shouldRenderProductionTile() ? product.tokenStaging : product.tokenProd
      ),
      product_surface_container: undefined,
      product_container_variant: undefined,
    },
    { enabled: !renderMockTile }
  );

  if (!renderMockTile && (isPending || !liveProductTileData)) {
    return null;
  }

  const { product_tiles, brands_by_token, request_id } =
    liveProductTileData ?? {};

  const productTiles: ILeanProductTile[] =
    (renderMockTile
      ? Array(visibleProductInputs.length).fill(MOCK_PRODUCT_TILE)
      : product_tiles) ?? [];
  const brands = (renderMockTile ? MOCK_BRAND : brands_by_token) ?? {};

  return (
    <Grid
      {...gridProps}
      builder-id={attributes["builder-id"]}
      className={`w-full ${attributes.className}`}
      key={attributes["key"]}
      style={attributes.style}
    >
      {productTiles?.map((productTile, index) => (
        <ProductTile
          key={`${productTile.product?.token}-${index}`}
          product={productTile}
          brand={brands?.[productTile.product?.brand_token ?? ""] ?? undefined}
          isLoggedIn={isLoggedIn}
          tileInteractions={
            isLoggedIn
              ? undefined
              : {
                  props: {
                    productLinkProps:
                      generateProductLinkPropsForLoggedOutRetailer({
                        productToken: productTile.product?.token,
                        brandToken: productTile.product?.brand_token,
                        location,
                      }),
                  },
                }
          }
          loggingContext={{
            impressionProviderProps: {
              ...loggingContext,
              context: context,
              containerName: "builder-io-product-tiles-section",
              requestId: request_id,
              screenContext: loggingContext.screenContext ?? Surface.BUILDER_IO,
              containerContext:
                loggingContext.containerContext ??
                ProductTileScreenContainer.BUILDER_IO_PRODUCTS,
            },
          }}
          type={
            visibleProductInputs[index]?.type ??
            ProductTileType.TILE_WITHOUT_BRAND
          }
        />
      ))}
    </Grid>
  );
};
